import dayjs, { Dayjs } from "dayjs"
import { ComplaintSource, IComplaint } from "./complaint.types"
import { TableExternalLink } from "@/types/TableCell"
import {
  COMPLAINT_CLOSED_STATUSES,
  COMPLAINT_OPEN_STATUSES,
  MAX_AGE_PROFILE_WEEKS_COUNT,
} from "@/constants/complaints"
import { Status } from "@/types/Status"

export class Complaint implements IComplaint {
  constructor(
    public id: number,
    public createdAt: Dayjs,
    public updatedAt: Dayjs,
    public externalIdentifier: string,
    public source?: ComplaintSource,
    public closedAt?: Dayjs,
    public deleted?: Dayjs,
    public team?: string,
    public agentEmail?: string,
    public agentName?: string,
    public ombudsmanCase?: boolean,
    public customerId?: number,
    public type?: string,
    public d1SignpostingCommsSent?: boolean,
    public sendDateOfD1SignpostingComms?: Dayjs,
    public d56SignpostingCommsSent?: boolean,
    public sendDateOfD56SignpostingComms?: Dayjs,
    public deadlockCommsSent?: boolean,
    public complaintsGoodwillAmount?: number,
    public category1?: string,
    public category2?: string,
    public category3?: string,
    public channel?: string,
    public dateRaised?: Dayjs,
    public statusText?: string,
    public subStatus?: string,
    public ofgemMappedComplaintCategory?: string,
    public internalComplaintCategory?: string,
    public complaintId?: string,
    public contactEmail?: string,
    public complaintsCustPrefContactMeth?: string,
    public billingAccountIds?: number[],
    public billingAccountNumber?: string,
    public sendDateOfRecentSignpostingComms?: Dayjs,
    public sendDateOfOverdueSignpostingComms?: Dayjs,
  ) {}

  get daysCountUntilClosed(): number {
    const isNotClosed =
      !this.statusText || !COMPLAINT_CLOSED_STATUSES.includes(this.statusText)
    if (isNotClosed || !this.dateRaised || !this.closedAt) return -1

    return this.closedAt.diff(this.dateRaised, "day")
  }

  get isClosed(): boolean {
    return (
      !!this.statusText &&
      COMPLAINT_CLOSED_STATUSES.includes(this.statusText) &&
      !!this.closedAt
    )
  }

  get status(): Status {
    if (this.isClosed) return new Status("default", "Closed")

    return new Status("caution", "Open")
  }

  get age(): string {
    const count = this.isClosed ? this.daysCountUntilClosed : this.daysCount
    if (!count) return "-"
    return `${count} ${count === 1 ? "day" : "days"}`
  }

  get ageProfile(): string {
    if (!this.daysCount || this.isClosed) {
      return "-"
    }

    const weeksCount = Math.min(
      Math.floor(this.daysCount / 7),
      MAX_AGE_PROFILE_WEEKS_COUNT,
    )

    return weeksCount === MAX_AGE_PROFILE_WEEKS_COUNT
      ? `${weeksCount}+ weeks`
      : `${weeksCount}-${weeksCount + 1} weeks`
  }

  get accountPath(): TableExternalLink | undefined {
    if (!this.billingAccountNumber || !Number(this.billingAccountNumber)) {
      return undefined
    }

    return {
      props: {
        text: this.billingAccountNumber,
        url: `/customer/${this.billingAccountNumber}`,
      },
    }
  }

  get externalIdentifierPath(): TableExternalLink | undefined {
    if (!this.externalIdentifier) {
      return undefined
    }

    const getUrl = () => {
      switch (this.source) {
        case "FRESHDESK":
          return `${
            import.meta.env.SO_NOVA_URL
          }/operations/freshdesk-contacts?search=${this.billingAccountNumber}`
        case "AWS_CONNECT":
          return `${
            import.meta.env.SO_AWS_CONNECT_ORIGIN
          }/cases/agent-app/case-detail/${this.externalIdentifier}`
        default:
          return null
      }
    }

    return {
      props: {
        text: this.externalIdentifier,
        url: getUrl(),
      },
    }
  }

  private get daysCount(): number | undefined {
    const isNotOpen =
      !this.statusText || !COMPLAINT_OPEN_STATUSES.includes(this.statusText)
    if (isNotOpen || !this.dateRaised) return undefined

    const daysCount = (this.closedAt || dayjs()).diff(this.dateRaised, "day")
    return daysCount > 0 ? daysCount : undefined
  }
}
